import React, { useRef, useState } from 'react'
import { useTheme } from '@react-navigation/native'
import { Icon, Text } from '@components'
import { useLanguage } from '@context'
import { COVER_ACCEPTED_TYPES, coverImageError, translateApiError } from '@utils'
import { useUploadEventCover } from '@hooks'

/** Same stack as react-native-web `<Text>` (native <button> does not inherit it). */
const FONT_STACK = '-apple-system, "system-ui", "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

interface CoverImageFieldProps {
  value: string
  onChange: (url: string) => void
}

/**
 * Web cover picker: drop zone at the size of the event page cover (click or drag
 * an image in), live upload progress, then the preview with "Change" / "Remove".
 */
export function CoverImageField({ value, onChange }: CoverImageFieldProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const upload = useUploadEventCover()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isOver, setIsOver] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [progress, setProgress] = useState(0)
  const [localPreview, setLocalPreview] = useState('')
  const [error, setError] = useState('')

  const preview = localPreview || value
  const isUploading = upload.isPending
  const highlighted = isOver || isDragging
  const openPicker = () => inputRef.current?.click()

  const send = (file: File) => {
    setError('')
    const problem = coverImageError(file)
    if (problem) {
      setError(t(`events.form.errors.${problem}`))
      return
    }
    const local = URL.createObjectURL(file)
    setLocalPreview(local)
    setProgress(0)
    const done = () => {
      setLocalPreview('')
      URL.revokeObjectURL(local)
    }
    upload.mutate(
      { file, onProgress: setProgress },
      {
        onSuccess: ({ url }) => {
          onChange(url)
          done()
        },
        onError: (e) => {
          setError(translateApiError(t, e).message)
          done()
        },
      },
    )
  }

  const pill: React.CSSProperties = {
    border: 'none',
    cursor: 'pointer',
    fontFamily: FONT_STACK,
    fontSize: theme.fontSize.extraSmall,
    fontWeight: 600,
    color: theme.colors.black,
    backgroundColor: theme.colors.white,
    padding: `${theme.spacing.xs + 2}px ${theme.spacing.md}px`,
    borderRadius: theme.border.jumbo,
    boxShadow: `0 1px 4px ${theme.colors.grey}`,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
      <div
        data-testid="event-cover-zone"
        role={preview ? undefined : 'button'}
        tabIndex={preview ? undefined : 0}
        onClick={preview ? undefined : openPicker}
        onKeyDown={(e) => {
          if (!preview && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            openPicker()
          }
        }}
        onMouseEnter={() => setIsOver(true)}
        onMouseLeave={() => setIsOver(false)}
        onDragEnter={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragging(false)
          const file = e.dataTransfer.files?.[0]
          if (file) send(file)
        }}
        style={{
          position: 'relative',
          height: 200,
          borderRadius: theme.border.large,
          overflow: 'hidden',
          boxSizing: 'border-box',
          cursor: preview ? 'default' : 'pointer',
          outline: 'none',
          border: preview
            ? `1px solid ${theme.colors.grey}`
            : `2px dashed ${highlighted ? theme.colors.primary : theme.colors.grey}`,
          backgroundColor: highlighted && !preview ? theme.colors.sidebar : theme.colors.white,
          transition: 'background-color 150ms, border-color 150ms',
        }}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                opacity: isUploading ? 0.6 : 1,
              }}
            />
            {isUploading ? (
              <>
                <div
                  style={{ position: 'absolute', top: theme.spacing.md, left: theme.spacing.md }}
                >
                  <span style={{ ...pill, cursor: 'default' }}>
                    {`${t('events.form.coverUploading')} ${progress}%`}
                  </span>
                </div>
                <div
                  role="progressbar"
                  aria-valuenow={progress}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: theme.spacing.xs + 2,
                    backgroundColor: theme.colors.grey,
                  }}
                >
                  <div
                    style={{
                      width: `${progress}%`,
                      height: '100%',
                      backgroundColor: theme.colors.primary,
                      transition: 'width 150ms',
                    }}
                  />
                </div>
              </>
            ) : (
              <div
                style={{
                  position: 'absolute',
                  top: theme.spacing.md,
                  right: theme.spacing.md,
                  display: 'flex',
                  gap: theme.spacing.sm,
                }}
              >
                <button
                  type="button"
                  style={pill}
                  onClick={openPicker}
                  data-testid="event-cover-change"
                >
                  {t('events.form.coverChange')}
                </button>
                <button
                  type="button"
                  style={pill}
                  data-testid="event-cover-remove"
                  onClick={() => {
                    setError('')
                    onChange('')
                  }}
                >
                  {t('events.form.coverRemove')}
                </button>
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm,
              padding: theme.spacing.lg,
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <Icon name="uploadCloud" size={theme.iconSize.jumbo} color={theme.colors.primary} />
            <Text type="bodyBold" color={theme.colors.black} center>
              {isDragging ? t('events.form.coverDropActive') : t('events.form.coverDrop')}
            </Text>
            <Text type="bodyExtraSmall" color={theme.colors.greyBlue} center>
              {t('events.form.coverUploadHint')}
            </Text>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={COVER_ACCEPTED_TYPES.join(',')}
          data-testid="event-cover-input"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (file) send(file)
          }}
        />
      </div>
      {error ? (
        <Text type="bodyExtraSmall" color={theme.colors.error} testID="event-cover-error">
          {error}
        </Text>
      ) : null}
    </div>
  )
}
